import React, { useState } from 'react';
import {
  CheckCircle, CheckSquare, BadgeCheck, ShieldCheck,
  Briefcase, Building2, Store, CreditCard, Package, Box, Truck, Wallet,
  User, Users, UserCheck, UserPlus, Heart, ThumbsUp, Smile, MessageCircle, MessageSquare,
  Award, Star, Trophy, Crown, Shield,
  Phone, Mail, Send, Bell, AtSign, Globe, Headphones,
  Clock, Calendar, CalendarCheck, Timer, AlarmClock,
  DollarSign, TrendingUp, TrendingDown, BarChart2, PieChart, Layers,
  Wrench, Settings, Code, Monitor, Smartphone, Laptop, Cpu, Database, Server, Cloud, Wifi, Zap, PenTool, Pencil, Clipboard,
  MapPin, Navigation, Compass, Map, Home, ArrowRight, Link,
  Lock, Key, Eye, EyeOff, Fingerprint,
  FileText, Image, Video, Camera, Mic, Music, BookOpen, GraduationCap,
  Leaf, Sun, Sparkles, Flame, Activity,
  Target, Flag, Download, Upload, RefreshCw, Search, Filter, ChevronRight,
  Scale, Handshake, Sword, Wand2, ShoppingCart, ShoppingBag, Gift, Recycle,
  Utensils, Coffee, Apple, Flower2, Droplets, Wind,
} from 'lucide-react';

export const ICON_OPTIONS = [
  // Check / validation
  'check-circle', 'check-square', 'badge-check', 'shield-check',
  // Business
  'briefcase', 'building-2', 'store', 'credit-card', 'package', 'box', 'truck', 'wallet',
  'shopping-cart', 'shopping-bag', 'gift',
  // People
  'user', 'users', 'user-check', 'user-plus', 'heart', 'thumbs-up', 'smile',
  'message-circle', 'message-square', 'handshake',
  // Achievement
  'award', 'star', 'trophy', 'crown', 'shield',
  // Communication
  'phone', 'mail', 'send', 'bell', 'at-sign', 'globe', 'headphones',
  // Time
  'clock', 'timer', 'calendar', 'calendar-check', 'alarm-clock',
  // Finance / analytics
  'dollar-sign', 'trending-up', 'trending-down', 'bar-chart-2', 'pie-chart', 'layers',
  // Tools / tech
  'wrench', 'settings', 'code', 'monitor', 'smartphone', 'laptop', 'cpu',
  'database', 'server', 'cloud', 'wifi', 'zap', 'pen-tool', 'pencil', 'clipboard',
  // Navigation / location
  'map-pin', 'navigation', 'compass', 'map', 'home', 'arrow-right', 'link',
  // Security
  'lock', 'key', 'eye', 'eye-off', 'fingerprint',
  // Content / media
  'file-text', 'image', 'video', 'camera', 'mic', 'music', 'book-open', 'graduation-cap',
  // Nature / lifestyle
  'leaf', 'sun', 'sparkles', 'flame', 'activity', 'recycle', 'flower-2', 'droplets', 'wind',
  'utensils', 'coffee', 'apple',
  // Misc
  'target', 'flag', 'download', 'upload', 'search', 'filter',
  'scale', 'sword', 'wand-2', 'chevron-right', 'refresh-cw',
];

export const ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  'check-circle': CheckCircle, 'check': CheckSquare, 'check-square': CheckSquare,
  'badge-check': BadgeCheck, 'shield-check': ShieldCheck,
  'briefcase': Briefcase, 'building-2': Building2, 'store': Store,
  'credit-card': CreditCard, 'package': Package, 'box': Box, 'truck': Truck, 'wallet': Wallet,
  'shopping-cart': ShoppingCart, 'shopping-bag': ShoppingBag, 'gift': Gift,
  'user': User, 'users': Users, 'user-check': UserCheck, 'user-plus': UserPlus,
  'heart': Heart, 'thumbs-up': ThumbsUp, 'smile': Smile,
  'message-circle': MessageCircle, 'message': MessageCircle, 'message-square': MessageSquare,
  'handshake': Handshake,
  'award': Award, 'star': Star, 'trophy': Trophy, 'crown': Crown, 'shield': Shield,
  'phone': Phone, 'mail': Mail, 'send': Send, 'bell': Bell, 'at-sign': AtSign,
  'globe': Globe, 'headphones': Headphones,
  'clock': Clock, 'timer': Timer, 'calendar': Calendar, 'calendar-check': CalendarCheck,
  'alarm-clock': AlarmClock,
  'dollar': DollarSign, 'dollar-sign': DollarSign, 'trending-up': TrendingUp,
  'trending-down': TrendingDown, 'chart': BarChart2, 'bar-chart': BarChart2,
  'bar-chart-2': BarChart2, 'pie-chart': PieChart, 'layers': Layers,
  'wrench': Wrench, 'tool': Wrench, 'settings': Settings, 'code': Code,
  'monitor': Monitor, 'smartphone': Smartphone, 'laptop': Laptop, 'cpu': Cpu,
  'database': Database, 'server': Server, 'cloud': Cloud, 'wifi': Wifi,
  'zap': Zap, 'bolt': Zap, 'pen-tool': PenTool, 'pencil': Pencil, 'clipboard': Clipboard,
  'map-pin': MapPin, 'location': MapPin, 'navigation': Navigation, 'compass': Compass,
  'map': Map, 'home': Home, 'arrow-right': ArrowRight, 'link': Link,
  'lock': Lock, 'key': Key, 'eye': Eye, 'eye-off': EyeOff, 'fingerprint': Fingerprint,
  'file': FileText, 'file-text': FileText, 'image': Image, 'video': Video,
  'camera': Camera, 'mic': Mic, 'music': Music, 'book-open': BookOpen,
  'graduation-cap': GraduationCap,
  'leaf': Leaf, 'sun': Sun, 'sparkles': Sparkles, 'flame': Flame, 'activity': Activity,
  'recycle': Recycle, 'flower-2': Flower2, 'droplets': Droplets, 'wind': Wind,
  'utensils': Utensils, 'coffee': Coffee, 'apple': Apple,
  'target': Target, 'flag': Flag, 'download': Download, 'upload': Upload,
  'refresh': RefreshCw, 'refresh-cw': RefreshCw, 'search': Search, 'filter': Filter,
  'chevron-right': ChevronRight,
  'scale': Scale, 'sword': Sword, 'wand': Wand2, 'wand-2': Wand2,
};

/**
 * Render an icon value on the canvas:
 * - Known Lucide name → renders the Lucide component
 * - Emoji / non-ASCII → renders as text
 * - Unknown string   → returns null (don't show raw label)
 */
export const renderSbIcon = (iconValue: string | undefined, size = 24): React.ReactNode => {
  if (!iconValue) return null;
  const Ic = ICON_MAP[iconValue.toLowerCase()];
  if (Ic) return <Ic size={size} />;
  // Emoji detection: any char with code point > 127
  if (/[^\x00-\x7F]/.test(iconValue)) {
    return <span style={{ fontSize: size * 0.9, lineHeight: 1 }}>{iconValue}</span>;
  }
  return null;
};

interface IconPickerFieldProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

const IconPickerField: React.FC<IconPickerFieldProps> = ({ value, onChange, placeholder = 'icon name' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const IconPreview = value ? (ICON_MAP[value.toLowerCase()] ?? null) : null;
  const filtered = search
    ? ICON_OPTIONS.filter(n => n.includes(search.toLowerCase()))
    : ICON_OPTIONS;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {/* Preview swatch */}
        <div style={{
          width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', border: '1px solid #e2e8f0', borderRadius: 6,
          background: '#f8fafc', color: '#475569',
        }}>
          {IconPreview ? <IconPreview size={14} /> : <span style={{ fontSize: 10, color: '#94a3b8' }}>?</span>}
        </div>
        {/* Text input */}
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          style={{
            flex: 1, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6,
            fontSize: '0.8125rem', background: '#fff', outline: 'none',
            boxSizing: 'border-box', minWidth: 0,
          }}
        />
        {/* Toggle button */}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{
            flexShrink: 0, width: 28, height: 28, border: '1px solid #e2e8f0',
            borderRadius: 6, background: open ? '#e0f2fe' : '#f8fafc',
            cursor: 'pointer', fontSize: 10, color: '#475569',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {open ? '▲' : '▼'}
        </button>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          marginTop: 4, background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          padding: 8,
        }}>
          {/* Search */}
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search icons…"
            autoFocus
            style={{
              width: '100%', padding: '5px 8px', border: '1px solid #e2e8f0',
              borderRadius: 6, fontSize: '0.75rem', background: '#f8fafc',
              outline: 'none', boxSizing: 'border-box', marginBottom: 8,
            }}
          />
          {/* Grid */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
            {filtered.map(name => {
              const Ic = ICON_MAP[name];
              const isSelected = value === name;
              return (
                <button
                  key={name}
                  title={name}
                  type="button"
                  onClick={() => { onChange(name); setOpen(false); setSearch(''); }}
                  style={{
                    width: 30, height: 30, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', border: isSelected ? '2px solid #2177FF' : '1px solid #e2e8f0',
                    borderRadius: 6, background: isSelected ? 'rgba(33,119,255,0.1)' : '#f8fafc',
                    cursor: 'pointer', padding: 0, color: isSelected ? '#2177FF' : '#475569',
                  }}
                >
                  {Ic ? <Ic size={14} /> : <span style={{ fontSize: 9 }}>{name[0].toUpperCase()}</span>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <span style={{ fontSize: '0.75rem', color: '#94a3b8', padding: '4px 0' }}>No icons match</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IconPickerField;
